import React from 'react';
import {
    Box,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Divider,
    Radio,
    RadioGroup,
    Paper,
    Chip,
} from '@mui/material';
import { SearchFacets, SearchResultType } from '../../services/searchService';
import { useTranslation } from '../../i18n';

interface SearchFiltersProps {
    facets: SearchFacets;
    selectedTypes: SearchResultType[];
    selectedDifficulty: string | null;
    onTypeChange: (types: SearchResultType[]) => void;
    onDifficultyChange: (difficulty: string | null) => void;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
    facets,
    selectedTypes,
    selectedDifficulty,
    onTypeChange,
    onDifficultyChange,
}) => {
    const { t } = useTranslation();

    const typeOptions: { value: SearchResultType; label: string; count: number }[] = [
        { value: 'course', label: t('search.types.course'), count: facets.types.courses || 0 },
        { value: 'lesson', label: t('search.types.lesson'), count: facets.types.lessons || 0 },
        { value: 'wiki', label: t('search.types.wiki'), count: facets.types.wiki || 0 },
        { value: 'learning_path', label: t('search.types.learningPath'), count: facets.types.learning_paths || 0 },
        { value: 'learning_group', label: t('search.types.learningGroup', 'Lerngruppe'), count: facets.types.learning_groups || 0 },
    ];

    const difficultyOptions = [
        { value: 'beginner', label: t('search.difficulty.beginner'), count: facets.difficulty.beginner },
        { value: 'intermediate', label: t('search.difficulty.intermediate'), count: facets.difficulty.intermediate },
        { value: 'advanced', label: t('search.difficulty.advanced'), count: facets.difficulty.advanced },
    ];

    const handleTypeToggle = (type: SearchResultType) => {
        if (selectedTypes.includes(type)) {
            if (selectedTypes.length > 1) {
                onTypeChange(selectedTypes.filter((t) => t !== type));
            }
        } else {
            onTypeChange([...selectedTypes, type]);
        }
    };

    const handleDifficultyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        onDifficultyChange(value === 'all' ? null : value);
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t('search.filters')}
            </Typography>

            {/* Content Type Filters */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('search.contentType')}
                </Typography>
                <FormGroup>
                    {typeOptions.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            control={
                                <Checkbox
                                    checked={selectedTypes.includes(option.value)}
                                    onChange={() => handleTypeToggle(option.value)}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{option.label}</span>
                                    <Chip label={option.count} size="small" variant="outlined" />
                                </Box>
                            }
                        />
                    ))}
                </FormGroup>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Difficulty Filters */}
            <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('search.difficultyLabel')}
                </Typography>
                <RadioGroup
                    value={selectedDifficulty || 'all'}
                    onChange={handleDifficultyChange}
                >
                    <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label={t('search.allDifficulties')}
                    />
                    {difficultyOptions.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio size="small" />}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>{option.label}</span>
                                    <Chip label={option.count} size="small" variant="outlined" />
                                </Box>
                            }
                        />
                    ))}
                </RadioGroup>
            </Box>
        </Paper>
    );
};

export default SearchFilters;
